import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Calendar, MapPin, Users, ArrowUpRight } from "lucide-react";
import { api, formatApiErrorDetail } from "../lib/api";
import { Reveal } from "../components/MaskedReveal";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "../components/ui/dialog";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";

function formatDate(d) {
  try {
    return new Date(d).toLocaleDateString("en-AU", { weekday: "long", day: "numeric", month: "long", year: "numeric" });
  } catch {
    return d;
  }
}

export default function Events() {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [active, setActive] = useState(null);
  const [form, setForm] = useState({ name: "", email: "", guests: 0, note: "" });
  const [submitting, setSubmitting] = useState(false);

  const load = () => {
    api
      .get("/events")
      .then((res) => setEvents(res.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, []);

  const openRsvp = (ev) => {
    setActive(ev);
    setForm({ name: "", email: "", guests: 0, note: "" });
  };

  const submitRsvp = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await api.post(`/events/${active.id}/rsvp`, {
        ...form,
        guests: Number(form.guests) || 0,
      });
      toast.success("Your spot is reserved — we'll be in touch!");
      setActive(null);
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section id="events" data-testid="events" className="relative py-28 md:py-40 bg-wine-surface/40 border-y border-white/10">
      <div className="mx-auto max-w-[1400px] px-6 md:px-12">
        <Reveal className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-16">
          <div>
            <span className="text-xs uppercase tracking-[0.28em] text-gold">Upcoming gatherings</span>
            <h2 className="mt-5 font-display leading-[0.95] tracking-tight text-cream text-[clamp(2.4rem,5vw,4.2rem)]">
              Reserve your <span className="italic text-terracotta">seat at the table</span>
            </h2>
          </div>
          <p className="max-w-sm text-smoke leading-relaxed">
            Spots are limited and kept intimate on purpose. RSVP to hold your place — no
            payment needed, we'll email the finer details.
          </p>
        </Reveal>

        {loading ? (
          <p className="text-smoke">Loading gatherings…</p>
        ) : events.length === 0 ? (
          <p data-testid="no-events" className="text-smoke">No gatherings scheduled just yet — join the society to be first to know.</p>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-12 gap-8">
            {events.map((ev, i) => (
              <motion.article
                key={ev.id}
                data-testid={`event-card-${i}`}
                initial={{ opacity: 0, y: 50 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-60px" }}
                transition={{ duration: 0.8, delay: i * 0.08, ease: [0.76, 0, 0.24, 1] }}
                className={`group relative flex flex-col overflow-hidden rounded-2xl border border-white/10 bg-wine-bg ${
                  i % 2 === 0 ? "md:col-span-7" : "md:col-span-5"
                }`}
              >
                <div className="relative h-64 md:h-80 overflow-hidden">
                  <img
                    src={ev.image}
                    alt={ev.title}
                    className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-wine-bg via-transparent to-transparent" />
                  <div className="absolute top-4 left-4 rounded-full bg-wine-bg/70 backdrop-blur px-4 py-1.5 text-xs uppercase tracking-[0.16em] text-gold border border-white/10">
                    {formatDate(ev.date)}
                  </div>
                  {ev.capacity > 0 && ev.rsvp_count >= ev.capacity && (
                    <div data-testid={`sold-out-badge-${i}`} className="absolute top-4 right-4 rounded-full bg-terracotta px-4 py-1.5 text-xs uppercase tracking-[0.16em] text-cream border border-white/10">
                      Sold out
                    </div>
                  )}
                </div>

                <div className="flex flex-1 flex-col p-7 md:p-9">
                  <h3 className="font-display text-2xl md:text-3xl text-cream tracking-tight">{ev.title}</h3>
                  <p className="mt-4 text-smoke leading-relaxed line-clamp-3">{ev.description}</p>

                  <div className="mt-6 flex flex-wrap gap-x-6 gap-y-2 text-sm text-smoke">
                    <span className="inline-flex items-center gap-2"><Calendar className="h-4 w-4 text-terracotta" /> {ev.time || "TBA"}</span>
                    <span className="inline-flex items-center gap-2"><MapPin className="h-4 w-4 text-terracotta" /> {ev.location}</span>
                    {ev.capacity > 0 && (
                      <span className="inline-flex items-center gap-2"><Users className="h-4 w-4 text-terracotta" /> {ev.rsvp_count}/{ev.capacity} reserved</span>
                    )}
                  </div>

                  {ev.capacity > 0 && ev.rsvp_count >= ev.capacity ? (
                    <button
                      data-testid={`rsvp-btn-${i}`}
                      disabled
                      className="mt-8 inline-flex w-fit items-center gap-2 rounded-full border border-white/15 px-6 py-3 text-sm font-semibold text-smoke cursor-not-allowed"
                    >
                      Fully booked
                    </button>
                  ) : (
                    <button
                      data-testid={`rsvp-btn-${i}`}
                      onClick={() => openRsvp(ev)}
                      className="mt-8 inline-flex w-fit items-center gap-2 rounded-full bg-terracotta px-6 py-3 text-sm font-semibold text-cream transition-all duration-300 hover:bg-gold hover:text-wine-bg hover:-translate-y-1"
                    >
                      Reserve a spot <ArrowUpRight className="h-4 w-4" />
                    </button>
                  )}
                </div>
              </motion.article>
            ))}
          </div>
        )}
      </div>

      <Dialog open={!!active} onOpenChange={(o) => !o && setActive(null)}>
        <DialogContent data-testid="rsvp-dialog" className="bg-wine-surface border-white/10 text-cream sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl">{active?.title}</DialogTitle>
            <DialogDescription className="text-smoke">
              Reserve your place. We'll email you the details soon.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={submitRsvp} className="space-y-4 mt-2">
            <div>
              <Label className="text-smoke">Full name</Label>
              <Input
                data-testid="rsvp-name"
                required
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                className="mt-1.5 bg-wine-bg border-white/15 text-cream"
              />
            </div>
            <div>
              <Label className="text-smoke">Email</Label>
              <Input
                data-testid="rsvp-email"
                type="email"
                required
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                className="mt-1.5 bg-wine-bg border-white/15 text-cream"
              />
            </div>
            <div>
              <Label className="text-smoke">Bringing guests?</Label>
              <Input
                data-testid="rsvp-guests"
                type="number"
                min="0"
                max="10"
                value={form.guests}
                onChange={(e) => setForm({ ...form, guests: e.target.value })}
                className="mt-1.5 bg-wine-bg border-white/15 text-cream"
              />
            </div>
            <div>
              <Label className="text-smoke">Anything we should know? (optional)</Label>
              <Textarea
                data-testid="rsvp-note"
                value={form.note}
                onChange={(e) => setForm({ ...form, note: e.target.value })}
                className="mt-1.5 bg-wine-bg border-white/15 text-cream"
              />
            </div>
            <button
              data-testid="rsvp-submit"
              disabled={submitting}
              className="w-full rounded-full bg-gold py-3 text-sm font-semibold text-wine-bg transition-all duration-300 hover:bg-cream disabled:opacity-60"
            >
              {submitting ? "Reserving…" : "Confirm my spot"}
            </button>
          </form>
        </DialogContent>
      </Dialog>
    </section>
  );
}
